import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './Explore.css';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import ExpertCard from '../../components/ExpertCard';
import './BookExpert.css';
import { authService, userService, therapistService } from '../../services';

// Helpers to normalize varying backend shapes (match BookExpert implementation)
const extractName = (r) => {
  if (!r) return '';
  const candidates = [r.display_name, r.displayName, r.DisplayName, r.name, r.fullName, r.userName, r.username, r.title];
  for (const c of candidates) if (c) return String(c);
  const nested = r.user || r.User || r.profile || r.Profile || r.person || r.Person;
  if (nested) {
    const nc = nested.display_name || nested.displayName || nested.full_name || nested.fullName || nested.name || nested.username;
    if (nc) return String(nc);
    if (nested.first_name || nested.last_name) return `${nested.first_name || nested.firstName || ''} ${nested.last_name || nested.lastName || ''}`.trim();
  }
  if ((r.first_name || r.firstName) || (r.last_name || r.lastName)) {
    return `${r.first_name || r.firstName || ''} ${r.last_name || r.lastName || ''}`.trim();
  }
  return '';
};

const extractYears = (r) => {
  if (!r) return null;
  const candidates = [r.years_of_experience, r.YearsOfExperience, r.yearsOfExperience, r.experienceYears, r.experience, r.years, r.exp];
  for (const c of candidates) {
    if (c === 0) return 0;
    if (c) {
      if (typeof c === 'number') return c;
      const m = String(c).match(/(\d+(?:[.,]\d+)?)/);
      if (m) return Number(m[1].replace(',', '.'));
      return String(c);
    }
  }
  const nested = r.profile || r.Profile || r.user || r.User || r.cv || r.CV;
  if (nested) {
    const v = nested.years_of_experience || nested.yearsOfExperience || nested.experienceYears || nested.experience;
    if (v === 0) return 0;
    if (v) {
      if (typeof v === 'number') return v;
      const ms = String(v).match(/(\d+(?:[\.,]\d+)?)/);
      if (ms) return Number(ms[1].replace(',', '.'));
      return String(v);
    }
  }
  return null;
};

const Explore = () => {
  const navigate = useNavigate();
  const [currentUser, setCurrentUser] = useState(null);
  const [currentStep, setCurrentStep] = useState(1);
  const [userPreferences, setUserPreferences] = useState({
    concerns: [],
    experience: '',
    sessionType: '',
    language: '',
    budget: '',
    timePreference: '',
    mentalHealthSymptoms: [],
    emotionalState: '',
    dailyImpact: '',
    supportSystem: ''
  });
  const [recommendedExperts, setRecommendedExperts] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  // All experts now come from the API. Keep as state so recommendation logic uses live data.
  const [allExperts, setAllExperts] = React.useState([]);

  // Load experts from API and normalize to the shape expected by recommendation logic
  useEffect(() => {
    let mounted = true;
    const loadExperts = async () => {
      try {
        const res = await therapistService.getAll();
        const raw = Array.isArray(res) ? res : res?.data || [];
        const normalized = raw.map(r => {
          const specialties = Array.isArray(r.specialization) ? r.specialization : (r.specializations || r.specialty ? String(r.specialization || r.specializations || r.specialty).split(',').map(s=>s.trim()) : []);
          const languages = Array.isArray(r.languages) ? r.languages : (r.language ? String(r.language).split(',').map(s=>s.trim()) : []);
          const sessionTypes = Array.isArray(r.sessionTypes) ? r.sessionTypes : (r.session_types ? (Array.isArray(r.session_types)? r.session_types : String(r.session_types).split(',').map(s=>s.trim())) : []);
          const yearsRaw = extractYears(r);
          const experienceStr = (typeof yearsRaw === 'number') ? `${yearsRaw} năm` : (typeof yearsRaw === 'string' && yearsRaw.length ? yearsRaw : '');
          return {
            id: r.id || r._id || r.UserId || r.userId || null,
            name: extractName(r) || (r.display_name || r.DisplayName || r.name || r.fullName || r.userName || ''),
            specialty: (specialties[0]) || r.specialty || '',
            experience: experienceStr,
            rating: r.rating ?? r.Rating ?? null,
            price: r.price || r.hourly_rate || r.price_per_session || null,
            image: r.image || r.avatar || null,
            description: r.description || r.bio || (r.qualifications && r.qualifications.join ? r.qualifications.join('. ') : ''),
            availability: r.availability || r.isAcceptingClients ? 'Có lịch' : 'Không rõ',
            specialties,
            languages,
            sessionTypes,
            priceRange: r.priceRange || r.price_range || 'medium',
            raw: r
          };
        });
        console.log('Loaded experts:', normalized.length, normalized);
        if (mounted) setAllExperts(normalized);
      } catch (err) {
        console.error('Failed to load experts for Explore page', err);
        // leave allExperts empty so UI shows no experts
      }
    };
    loadExperts();
    return () => { mounted = false; };
  }, []);

  // Available options for user preferences
  const concernOptions = [
    { id: 'anxiety', label: 'Lo âu và căng thẳng', description: 'Cảm giác lo lắng, bồn chồn, khó thở' },
    { id: 'depression', label: 'Trầm cảm', description: 'Cảm giác buồn bã, mất hứng thú kéo dài' },
    { id: 'relationships', label: 'Mối quan hệ', description: 'Khó khăn trong giao tiếp, xung đột với người thân' },
    { id: 'family', label: 'Gia đình', description: 'Xung đột gia đình, nuôi dạy con cái' },
    { id: 'trauma', label: 'Chấn thương tâm lý', description: 'Trải qua sự kiện đau thương, PTSD' },
    { id: 'addiction', label: 'Cai nghiện', description: 'Lạm dụng chất, nghiện game, nghiện mạng xã hội' },
    { id: 'children', label: 'Trẻ em & Thanh thiếu niên', description: 'Hành vi, học tập, giao tiếp xã hội' },
    { id: 'elderly', label: 'Người cao tuổi', description: 'Sa sút trí tuệ, cô đơn, thay đổi tâm lý' }
  ];

  const experienceOptions = [
    { id: 'beginner', label: 'Mới bắt đầu', description: 'Lần đầu tìm kiếm hỗ trợ tâm lý' },
    { id: 'some', label: 'Có một chút kinh nghiệm', description: 'Đã từng tham gia trị liệu trước đó' },
    { id: 'experienced', label: 'Có kinh nghiệm', description: 'Thường xuyên tham gia trị liệu' }
  ];

  const sessionTypeOptions = [
    { 
      id: 'video', 
      label: 'Video call', 
      description: 'Trị liệu qua video, tiện lợi và riêng tư tại nhà' 
    },
    { 
      id: 'phone', 
      label: 'Cuộc gọi điện thoại', 
      description: 'Trị liệu qua điện thoại, không cần internet ổn định' 
    },
    { 
      id: 'in-person', 
      label: 'Gặp trực tiếp', 
      description: 'Trị liệu trực tiếp tại phòng khám chuyên nghiệp' 
    }
  ];

  const languageOptions = [
    { id: 'vi', label: 'Tiếng Việt' },
    { id: 'en', label: 'English' },
    { id: 'both', label: 'Cả hai' }
  ];

  const budgetOptions = [
    { id: 'low', label: 'Dưới 2 triệu VNĐ', range: '1-2M' },
    { id: 'medium', label: '2-3 triệu VNĐ', range: '2-3M' },
    { id: 'high', label: '3-4 triệu VNĐ', range: '3-4M' },
    { id: 'premium', label: 'Trên 4 triệu VNĐ', range: '4M+' }
  ];

  const timePreferenceOptions = [
    { id: 'morning', label: 'Buổi sáng (8-12h)', description: 'Thời gian tỉnh táo, năng lượng cao' },
    { id: 'afternoon', label: 'Buổi chiều (12-17h)', description: 'Sau giờ làm việc, thư giãn' },
    { id: 'evening', label: 'Buổi tối (17-21h)', description: 'Sau giờ làm, riêng tư' },
    { id: 'flexible', label: 'Linh hoạt', description: 'Có thể sắp xếp theo lịch của chuyên gia' }
  ];

  // New mental health assessment options
  const mentalHealthSymptomsOptions = [
    { 
      id: 'sleep_issues', 
      label: 'Rối loạn giấc ngủ', 
      description: 'Khó ngủ, ngủ không sâu, ảnh hưởng đến năng lượng hàng ngày' 
    },
    { 
      id: 'appetite_changes', 
      label: 'Thay đổi khẩu vị', 
      description: 'Mất cảm giác ngon miệng hoặc ăn quá nhiều để giải tỏa cảm xúc' 
    },
    { 
      id: 'concentration_problems', 
      label: 'Khó tập trung', 
      description: 'Không thể tập trung vào công việc, học tập hoặc hoạt động thường ngày' 
    },
    { 
      id: 'mood_swings', 
      label: 'Thay đổi tâm trạng', 
      description: 'Cảm xúc thay đổi đột ngột, không thể kiểm soát' 
    },
    { 
      id: 'social_withdrawal', 
      label: 'Thu mình xã hội', 
      description: 'Tránh gặp gỡ bạn bè, thích ở một mình' 
    },
    { 
      id: 'physical_symptoms', 
      label: 'Triệu chứng thể chất', 
      description: 'Đau đầu, mệt mỏi không rõ nguyên nhân' 
    }
  ];

  const emotionalStateOptions = [
    { 
      id: 'overwhelmed', 
      label: 'Cảm thấy quá tải', 
      description: 'Có quá nhiều trách nhiệm và áp lực, cảm thấy không thể xử lý' 
    },
    { 
      id: 'hopeless', 
      label: 'Cảm thấy vô vọng', 
      description: 'Không thấy tương lai tươi sáng, mất niềm tin vào khả năng thay đổi' 
    },
    { 
      id: 'anxious', 
      label: 'Lo lắng thường xuyên', 
      description: 'Luôn cảm thấy bồn chồn, lo lắng về tương lai' 
    },
    { 
      id: 'angry', 
      label: 'Tức giận, cáu kỉnh', 
      description: 'Dễ nổi nóng, không thể kiểm soát cơn giận' 
    },
    { 
      id: 'empty', 
      label: 'Cảm thấy trống rỗng', 
      description: 'Không có cảm xúc gì, không thấy hứng thú với bất cứ điều gì' 
    },
    { 
      id: 'confused', 
      label: 'Bối rối, mất phương hướng', 
      description: 'Không biết mình muốn gì, cần gì hoặc nên làm gì' 
    }
  ];

  const dailyImpactOptions = [
    { 
      id: 'work_performance', 
      label: 'Hiệu suất công việc', 
      description: 'Không thể tập trung, thường xuyên mắc lỗi, thiếu động lực' 
    },
    { 
      id: 'relationships', 
      label: 'Mối quan hệ', 
      description: 'Khó khăn trong giao tiếp, các mối quan hệ trở nên căng thẳng' 
    },
    { 
      id: 'daily_routines', 
      label: 'Sinh hoạt hàng ngày', 
      description: 'Không thể duy trì thói quen cơ bản, cuộc sống hỗn loạn' 
    },
    { 
      id: 'health_habits', 
      label: 'Sức khỏe thể chất', 
      description: 'Bỏ bê tập thể dục, ăn uống không lành mạnh' 
    },
    { 
      id: 'social_activities', 
      label: 'Hoạt động xã hội', 
      description: 'Tránh các hoạt động xã hội, cảm thấy không thoải mái với đám đông' 
    },
    { 
      id: 'future_planning', 
      label: 'Kế hoạch tương lai', 
      description: 'Không thể lập kế hoạch, cảm thấy không có hy vọng' 
    }
  ];

  const supportSystemOptions = [
    { 
      id: 'family_support', 
      label: 'Hỗ trợ từ gia đình', 
      description: 'Có gia đình hiểu biết và sẵn sàng hỗ trợ' 
    },
    { 
      id: 'friends_support', 
      label: 'Hỗ trợ từ bạn bè', 
      description: 'Có bạn bè thân thiết có thể chia sẻ và lắng nghe' 
    },
    { 
      id: 'professional_support', 
      label: 'Hỗ trợ chuyên nghiệp', 
      description: 'Đang được hỗ trợ bởi chuyên gia tâm lý, bác sĩ tâm thần' 
    },
    { 
      id: 'community_support', 
      label: 'Hỗ trợ cộng đồng', 
      description: 'Tham gia nhóm hỗ trợ, câu lạc bộ cộng đồng' 
    },
    { 
      id: 'limited_support', 
      label: 'Hỗ trợ hạn chế', 
      description: 'Không có nhiều người để chia sẻ, cảm thấy cô đơn' 
    },
    { 
      id: 'online_support', 
      label: 'Hỗ trợ trực tuyến', 
      description: 'Sử dụng nền tảng trực tuyến, ứng dụng để tìm hỗ trợ' 
    }
  ];

  // Load current user
  useEffect(() => {
    const user = authService.getCurrentUser();
    if (user) {
      setCurrentUser(user);
    } else {
      navigate('/login');
    }
  }, [navigate]);

  // Algorithm to recommend experts based on user preferences
  const getRecommendedExperts = () => {
    // If no experts available, return empty array (should not happen in normal flow)
    if (!allExperts || allExperts.length === 0) {
      console.warn('No experts available for recommendation');
      return [];
    }
    
    let scoredExperts = allExperts.map(expert => {
      let score = 0;
      
      // Specialty matching (highest weight - 30 points max)
      userPreferences.concerns.forEach(concern => {
        if (expert.specialties.includes(concern)) {
          score += 15; // High weight for specialty matching
        }
      });
      
      // Language matching (10 points)
      if (userPreferences.language === 'both' || expert.languages.includes(userPreferences.language)) {
        score += 10;
      }
      
      // Session type matching (10 points)
      if (expert.sessionTypes.includes(userPreferences.sessionType)) {
        score += 10;
      }
      
      // Budget matching (15 points)
      if (expert.priceRange === userPreferences.budget) {
        score += 15;
      } else if (userPreferences.budget === 'low' && expert.priceRange === 'medium') {
        score += 8;
      } else if (userPreferences.budget === 'medium' && expert.priceRange === 'high') {
        score += 8;
      }
      
      // Experience level bonus (10 points)
      if (userPreferences.experience === 'beginner' && (expert.experience.includes('6') || expert.experience.includes('7'))) {
        score += 10; // Prefer less experienced for beginners
      } else if (userPreferences.experience === 'experienced' && (expert.experience.includes('10') || expert.experience.includes('15') || expert.experience.includes('20'))) {
        score += 10; // Prefer more experienced for experienced users
      }
      
      // Rating bonus (5 points)
      if (expert.rating >= 4.8) {
        score += 5;
      }
      
      // Availability bonus (5 points)
      if (expert.availability.includes('hôm nay') || expert.availability.includes('tuần này')) {
        score += 5;
      }
      
      return { ...expert, score };
    });
    
    // Sort all experts by score (highest first)
    const sortedExperts = scoredExperts.sort((a, b) => b.score - a.score);
    
    // Always ensure we have at least 2 experts
    let finalExperts = [];
    
    // First, add all scored experts (even with low scores)
    finalExperts = [...sortedExperts];
    
    // If we have less than 2 experts, fill with highest rated experts from allExperts
    if (finalExperts.length < 2) {
      const additionalExperts = allExperts
        .filter(expert => !finalExperts.some(existing => existing.id === expert.id))
        .sort((a, b) => (b.rating || 0) - (a.rating || 0))
        .slice(0, 2 - finalExperts.length);
      
      finalExperts = [...finalExperts, ...additionalExperts];
    }
    
    // If we still have less than 2 experts, add any remaining experts
    if (finalExperts.length < 2 && allExperts.length > 0) {
      const remainingExperts = allExperts
        .filter(expert => !finalExperts.some(existing => existing.id === expert.id))
        .slice(0, 2 - finalExperts.length);
      
      finalExperts = [...finalExperts, ...remainingExperts];
    }
    
    // Final fallback: if we still have no experts, return the first expert from allExperts
    if (finalExperts.length === 0 && allExperts.length > 0) {
      finalExperts = [allExperts[0]];
    }
    
    // Return top 3 experts, but ensure we have at least 1-2
    const result = finalExperts.slice(0, Math.max(1, Math.min(3, finalExperts.length)));
    console.log('Recommended experts:', result.length, result);
    return result;
  };

  const handleConcernToggle = (concernId) => {
    setUserPreferences(prev => ({
      ...prev,
      concerns: prev.concerns.includes(concernId)
        ? prev.concerns.filter(id => id !== concernId)
        : [...prev.concerns, concernId]
    }));
  };

  const handlePreferenceChange = (key, value) => {
    setUserPreferences(prev => ({
      ...prev,
      [key]: value
    }));
    
    // Auto-advance to next step for time preference (step 10)
    if (key === 'timePreference') {
      console.log('Time preference selected, auto-advancing to next step');
      setTimeout(() => {
        handleNextStep();
      }, 500);
    }
  };

  const handleMentalHealthSymptomsToggle = (symptomId) => {
    setUserPreferences(prev => ({
      ...prev,
      mentalHealthSymptoms: prev.mentalHealthSymptoms.includes(symptomId)
        ? prev.mentalHealthSymptoms.filter(id => id !== symptomId)
        : [...prev.mentalHealthSymptoms, symptomId]
    }));
  };

  const handleNextStep = () => {
    console.log('handleNextStep called, currentStep:', currentStep);
    if (currentStep < 11) {
      setCurrentStep(currentStep + 1);
    } else {
      // Generate recommendations
      console.log('Generating recommendations...');
      setIsLoading(true);
      setTimeout(() => {
        const recommendations = getRecommendedExperts();
        console.log('Generated recommendations:', recommendations.length, recommendations);
        setRecommendedExperts(recommendations);
        setIsLoading(false);
        setCurrentStep(11); // Show results
      }, 1500);
    }
  };

  const handlePreviousStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleBookExpert = (expert) => {
    navigate('/booking-dashboard', { state: { expert: expert } });
  };

  const handleMaybeLater = () => {
    navigate('/book-expert');
  };

  const handleSaveResults = async () => {
    const currentUser = authService.getCurrentUser();
    if (!currentUser?.id) {
      alert('Vui lòng đăng nhập để lưu kết quả.');
      navigate('/login');
      return;
    }

    // Build a lightweight payload containing preferences and recommendations
    const payload = {
      preferences: userPreferences,
      recommendations: recommendedExperts.map(e => ({ id: e.id, name: e.name, specialty: e.specialty, rating: e.rating }))
    };

    try {
      document.body.style.cursor = 'wait';
      await userService.savePsychometric(currentUser.id, payload);
      alert('Kết quả đã được lưu thành công.');
    } catch (err) {
      console.error('Save psychometric error:', err);
      alert(err?.response?.data?.message || err?.message || 'Không thể lưu kết quả');
    } finally {
      document.body.style.cursor = 'default';
    }
  };

  const handleRetrySelection = () => {
    // Reset all user preferences
    setUserPreferences({
      concerns: [],
      experience: '',
      sessionType: '',
      language: '',
      budget: '',
      timePreference: '',
      mentalHealthSymptoms: [],
      emotionalState: '',
      dailyImpact: '',
      supportSystem: ''
    });
    setRecommendedExperts([]);
    setCurrentStep(1);
  };

  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="step-content">
            <h2>Bạn đang gặp vấn đề gì?</h2>
            <p>Chọn tất cả các vấn đề phù hợp với bạn</p>
            <div className="options-grid">
              {concernOptions.map(option => (
                <button
                  key={option.id}
                  className={`option-card ${userPreferences.concerns.includes(option.id) ? 'selected' : ''}`}
                  onClick={() => handleConcernToggle(option.id)}
                >
                  <span className="option-label">{option.label}</span>
                  <span className="option-description">{option.description}</span>
                </button>
              ))}
            </div>
          </div>
        );

      case 2:
        return (
          <div className="step-content">
            <h2>Triệu chứng sức khỏe tâm lý</h2>
            <p>Chọn tất cả các triệu chứng bạn đang gặp phải</p>
            <div className="options-list">
              {mentalHealthSymptomsOptions.map(option => (
                <button
                  key={option.id}
                  className={`option-item ${userPreferences.mentalHealthSymptoms.includes(option.id) ? 'selected' : ''}`}
                  onClick={() => handleMentalHealthSymptomsToggle(option.id)}
                >
                  <div className="option-header">
                    <span className="option-label">{option.label}</span>
                  </div>
                  <p className="option-description">{option.description}</p>
                </button>
              ))}
            </div>
          </div>
        );

      case 3:
        return (
          <div className="step-content">
            <h2>Trạng thái cảm xúc hiện tại</h2>
            <p>Mô tả cảm xúc chủ đạo bạn đang trải qua</p>
            <div className="options-list">
              {emotionalStateOptions.map(option => (
                <button
                  key={option.id}
                  className={`option-item ${userPreferences.emotionalState === option.id ? 'selected' : ''}`}
                  onClick={() => handlePreferenceChange('emotionalState', option.id)}
                >
                  <div className="option-header">
                    <span className="option-label">{option.label}</span>
                  </div>
                  <p className="option-description">{option.description}</p>
                </button>
              ))}
            </div>
          </div>
        );

      case 4:
        return (
          <div className="step-content">
            <h2>Tác động đến cuộc sống hàng ngày</h2>
            <p>Vấn đề tâm lý ảnh hưởng đến lĩnh vực nào trong cuộc sống của bạn?</p>
            <div className="options-list">
              {dailyImpactOptions.map(option => (
                <button
                  key={option.id}
                  className={`option-item ${userPreferences.dailyImpact === option.id ? 'selected' : ''}`}
                  onClick={() => handlePreferenceChange('dailyImpact', option.id)}
                >
                  <div className="option-header">
                    <span className="option-label">{option.label}</span>
                  </div>
                  <p className="option-description">{option.description}</p>
                </button>
              ))}
            </div>
          </div>
        );

      case 5:
        return (
          <div className="step-content">
            <h2>Hệ thống hỗ trợ</h2>
            <p>Bạn có những nguồn hỗ trợ nào trong cuộc sống?</p>
            <div className="options-list">
              {supportSystemOptions.map(option => (
                <button
                  key={option.id}
                  className={`option-item ${userPreferences.supportSystem === option.id ? 'selected' : ''}`}
                  onClick={() => handlePreferenceChange('supportSystem', option.id)}
                >
                  <div className="option-header">
                    <span className="option-label">{option.label}</span>
                  </div>
                  <p className="option-description">{option.description}</p>
                </button>
              ))}
            </div>
          </div>
        );

      case 6:
        return (
          <div className="step-content">
            <h2>Kinh nghiệm của bạn với trị liệu</h2>
            <p>Điều này giúp chúng tôi đề xuất chuyên gia phù hợp</p>
            <div className="options-list">
              {experienceOptions.map(option => (
                <button
                  key={option.id}
                  className={`option-item ${userPreferences.experience === option.id ? 'selected' : ''}`}
                  onClick={() => handlePreferenceChange('experience', option.id)}
                >
                  <div className="option-header">
                    <span className="option-label">{option.label}</span>
                  </div>
                  <p className="option-description">{option.description}</p>
                </button>
              ))}
            </div>
          </div>
        );

      case 7:
        return (
          <div className="step-content">
            <h2>Bạn muốn loại buổi học nào?</h2>
            <p>Chọn phương thức bạn cảm thấy thoải mái nhất</p>
            <div className="options-grid">
              {sessionTypeOptions.map(option => (
                <button
                  key={option.id}
                  className={`option-card ${userPreferences.sessionType === option.id ? 'selected' : ''}`}
                  onClick={() => handlePreferenceChange('sessionType', option.id)}
                >
                  <span className="option-label">{option.label}</span>
                  <span className="option-description">{option.description}</span>
                </button>
              ))}
            </div>
          </div>
        );

      case 8:
        return (
          <div className="step-content">
            <h2>Ngôn ngữ ưa thích</h2>
            <p>Chọn ngôn ngữ bạn muốn sử dụng trong buổi trị liệu</p>
            <div className="options-list">
              {languageOptions.map(option => (
                <button
                  key={option.id}
                  className={`option-item ${userPreferences.language === option.id ? 'selected' : ''}`}
                  onClick={() => handlePreferenceChange('language', option.id)}
                >
                  <span className="option-label">{option.label}</span>
                </button>
              ))}
            </div>
          </div>
        );

      case 9:
        return (
          <div className="step-content">
            <h2>Ngân sách của bạn</h2>
            <p>Chọn khoảng giá phù hợp với ngân sách của bạn</p>
            <div className="options-list">
              {budgetOptions.map(option => (
                <button
                  key={option.id}
                  className={`option-item ${userPreferences.budget === option.id ? 'selected' : ''}`}
                  onClick={() => handlePreferenceChange('budget', option.id)}
                >
                  <div className="option-header">
                    <span className="option-label">{option.label}</span>
                    <span className="option-range">{option.range}</span>
                  </div>
                </button>
              ))}
            </div>
          </div>
        );

      case 10:
        return (
          <div className="step-content">
            <h2>Thời gian ưa thích</h2>
            <p>Khi nào bạn muốn có buổi trị liệu?</p>
            <div className="options-grid">
              {timePreferenceOptions.map(option => (
                <button
                  key={option.id}
                  className={`option-card ${userPreferences.timePreference === option.id ? 'selected' : ''}`}
                  onClick={() => handlePreferenceChange('timePreference', option.id)}
                >
                  <span className="option-label">{option.label}</span>
                  <span className="option-description">{option.description}</span>
                </button>
              ))}
            </div>
          </div>
        );

      case 11:
        // Auto-generate recommendations if not already done
        if (recommendedExperts.length === 0 && !isLoading && allExperts.length > 0) {
          console.log('Auto-generating recommendations for step 11');
          const recommendations = getRecommendedExperts();
          setRecommendedExperts(recommendations);
        }
        
        return (
          <div className="step-content">
            <h2>Chuyên gia được đề xuất cho bạn</h2>
            <p>Dựa trên thông tin bạn cung cấp, đây là những chuyên gia phù hợp nhất</p>
            {isLoading ? (
              <div className="loading-container">
                <div className="loading-spinner"></div>
                <p>Đang tìm kiếm chuyên gia phù hợp...</p>
              </div>
            ) : (
              <>
                <div className="experts-recommendations">
                  {console.log('Rendering experts:', recommendedExperts.length, recommendedExperts)}
                  {recommendedExperts.map((expert, index) => (
                        <ExpertCard
                          key={expert.id || expert.name}
                          expert={{ ...expert, badge: index === 0 ? 'TỐT NHẤT' : index === 1 ? 'PHỔ BIẾN' : 'MỚI' }}
                          isFavorite={false}
                          onToggleFavorite={() => {}}
                          onBook={() => handleBookExpert(expert)}
                          bookLabel="Đặt Lịch"
                        />
                      ))}
                </div>
                
                {/* Button Container - Outside experts section */}
                <div style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                width: '100%',
                marginTop: '40px',
                padding: '30px 0',
                borderTop: '1px solid #e0e0e0'
              }}>
                <div style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: '20px',
                  maxWidth: '350px',
                  width: '100%'
                }}>
                  {/* Main Action Button */}
                  <button
                    className="take-lesson-btn"
                    id="explore-top-save-results"
                    onClick={handleSaveResults}
                    style={{
                      width: '100%',
                      maxWidth: '280px',
                      padding: '16px 32px',
                      fontSize: '16px',
                      fontWeight: 'bold',
                      borderRadius: '10px',
                      border: 'none',
                      backgroundColor: '#ff6b35',
                      color: 'white',
                      cursor: 'pointer',
                      boxShadow: '0 4px 8px rgba(255, 107, 53, 0.3)',
                      transition: 'all 0.3s ease'
                    }}
                  >
                    Lưu kết quả
                  </button>
                  
                  {/* Secondary Action Buttons */}
                  <div style={{
                    display: 'flex',
                    gap: '20px',
                    width: '100%',
                    justifyContent: 'center'
                  }}>
                    <button 
                      className="retry-selection-btn"
                      onClick={handleRetrySelection}
                    >
                      Chọn lại
                    </button>
                    <button 
                      className="maybe-later-btn"
                      onClick={handleMaybeLater}
                      style={{
                        flex: '1',
                        maxWidth: '130px',
                        padding: '14px 24px',
                        fontSize: '14px',
                        fontWeight: '500',
                        borderRadius: '8px',
                        border: '2px solid #ff6b35',
                        backgroundColor: 'white',
                        color: '#ff6b35',
                        cursor: 'pointer',
                        transition: 'all 0.3s ease'
                      }}
                    >
                      Có thể sau
                    </button>
                  </div>
                </div>
              </div>
              </>
            )}
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="explore-page page-with-header">
      <Header />
      
      <div className="main-content">
        <div className="explore-container">
          <div className="progress-bar">
            <div 
              className="progress-fill" 
              style={{ width: `${(currentStep / 11) * 100}%` }}
            ></div>
          </div>
          
          <div className="step-indicator">
            Bước {currentStep} / 11
          </div>
          
          {renderStep()}
          
          <div className="step-navigation">
            {currentStep > 1 && currentStep < 11 && (
              <button 
                className="prev-btn"
                onClick={handlePreviousStep}
              >
                Quay lại
              </button>
            )}
            
            {currentStep < 10 && (
              <button 
                className="next-btn"
                onClick={handleNextStep}
                disabled={
                  (currentStep === 1 && userPreferences.concerns.length === 0) ||
                  (currentStep === 2 && userPreferences.mentalHealthSymptoms.length === 0) ||
                  (currentStep === 3 && !userPreferences.emotionalState) ||
                  (currentStep === 4 && !userPreferences.dailyImpact) ||
                  (currentStep === 5 && !userPreferences.supportSystem) ||
                  (currentStep === 6 && !userPreferences.experience) ||
                  (currentStep === 7 && !userPreferences.sessionType) ||
                  (currentStep === 8 && !userPreferences.language) ||
                  (currentStep === 9 && !userPreferences.budget) ||
                  (currentStep === 10 && !userPreferences.timePreference)
                }
              >
                Tiếp theo
              </button>
            )}
          </div>
        </div>
      </div>
      
      <Footer />
      {/* Floating Save button visible on step 7 */}
      {currentStep === 7 && (
        <button
          id="floating-save-btn"
          className="floating-save-btn"
          onClick={handleSaveResults}
          aria-label="Lưu kết quả"
        >
          Lưu kết quả
        </button>
      )}
    </div>
  );
};

export default Explore;